import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, CheckCheck, ArrowRight, Check, X, Pencil, Settings2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import NotifikaceKarta from '../components/notifikace/Karta';
import { vzhledModulu, cilNotifikace, jeAkcniNotifikace, cestaRozhodnuti, popiskyRozhodnuti, upravitelnaNepritomnost, barvaTypu } from '../lib/notifikace';
import UpravitNepritomnost from '../components/notifikace/UpravitNepritomnost';
import NastaveniUpozorneni from '../components/notifikace/NastaveniUpozorneni';

// ── Typ (info/success/warning/error) ───────────────────────────
// ── Helpers ────────────────────────────────────────────────────
function relativeTime(iso) {
    if (!iso) return '';
    const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (min < 1)  return 'právě teď';
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24)   return `${h} h`;
    const d = Math.floor(h / 24);
    if (d < 30)   return `${d} d`;
    return `${Math.floor(d / 30)} měs.`;
}

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

const LIMIT = 50;

// ── Hlavní komponenta ──────────────────────────────────────────
/**
 * @param {Array} [ukoly] - co čeká mimo notifikace: nepodepsané směrnice,
 *   dokumenty k podpisu, nedokončená školení. Na mobilu se mezi Přehledem
 *   a Notifikacemi přepíná, takže dvě schránky nevyřízeného znamenaly, že
 *   člověk musí vědět, že jsou dvě — a po vyřízení nevěděl, kde se to
 *   odškrtává. Na desktopu jsou obě vidět naráz vedle sebe, tam se nepředávají.
 *   Tvar položky: { id, title, subtitle, icon, iconColor, deadline, onClick }.
 */
export default function Aktivita({ onNavigate, user, ukoly = [] }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    const [notifikace, setNotifikace]       = useState([]);
    const [loading, setLoading]             = useState(true);
    const [loadingMore, setLoadingMore]     = useState(false);
    const [total, setTotal]                 = useState(0);
    const [hasMore, setHasMore]             = useState(false);
    const [offset, setOffset]               = useState(0);
    const [activeModul, setActiveModul]     = useState('vse');
    const [zobrazOdbyte, setZobrazOdbyte]   = useState(false);
    const [availableModuly, setAvailableModuly] = useState([]);
    // Nastavení upozornění podle uživatele (2026-10-23)
    const [nastaveniOtevreno, setNastaveniOtevreno] = useState(false);
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Počet nepřečtených pro header
    const unreadCount = notifikace.filter(n => !n.precten).length;

    const fetchData = useCallback((modul, off = 0, append = false) => {
        if (!append) setLoading(true);
        else setLoadingMore(true);

        const params = new URLSearchParams({ limit: LIMIT, offset: off });
        if (modul !== 'vse') params.set('modul', modul);

        fetch(`/api/notifikace?${params}`)
            .then(r => r.json())
            .then(result => {
                setNotifikace(prev => append ? [...prev, ...result.data] : result.data);
                setTotal(result.total);
                setHasMore(result.has_more);
                setOffset(off + LIMIT);
                setLoading(false);
                setLoadingMore(false);
            })
            .catch(() => { setLoading(false); setLoadingMore(false); });
    }, []);

    // Initial load
    useEffect(() => {
        fetch('/api/notifikace/moduly').then(r => r.json()).then(setAvailableModuly).catch(() => {});
        fetchData('vse');
    }, [fetchData]);

    // Real-time refresh: push, nová notifikace nebo vyřízení na serveru (app.jsx → `notifikace-seznam`)
    useEffect(() => {
        const obnovit = () => fetchData(activeModul);
        const handleSwMsg = (e) => {
            if (e.data?.type === 'PUSH_RECEIVED') obnovit();
        };
        navigator.serviceWorker?.addEventListener('message', handleSwMsg);
        window.addEventListener('notifikace-seznam', obnovit);
        return () => {
            navigator.serviceWorker?.removeEventListener('message', handleSwMsg);
            window.removeEventListener('notifikace-seznam', obnovit);
        };
    }, [fetchData, activeModul]);

    // Filter change
    const handleFilterChange = (modul) => {
        setActiveModul(modul);
        setOffset(0);
        fetchData(modul);
    };

    const loadMore = () => fetchData(activeModul, offset, true);

    // Hlídka konce seznamu. `loadMore` se mění s každým renderem, takže se drží
    // v ref — jinak by se pozorovatel zakládal pořád dokola.
    const hlidka = useRef(null);
    const nacistRef = useRef(loadMore);
    nacistRef.current = loadMore;
    useEffect(() => {
        const el = hlidka.current;
        if (!el || !hasMore || loadingMore) return;   // hlídka se nekreslí, když není co dotahovat
        const io = new IntersectionObserver(([z]) => { if (z.isIntersecting) nacistRef.current(); }, { rootMargin: '200px' });
        io.observe(el);
        return () => io.disconnect();
    }, [hasMore, loadingMore, notifikace.length]);

    const markRead = (id) => {
        fetch(`/api/notifikace/${id}/precist`, { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf() } })
            .then(() => {
                setNotifikace(prev => prev.map(n => n.id === id ? { ...n, precten: true } : n));
                window.dispatchEvent(new Event('notifikace-changed'));
            });
    };

    const markAllRead = () => {
        fetch('/api/notifikace/precist-vse', { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf() } })
            .then(() => {
                setNotifikace(prev => prev.map(n => ({ ...n, precten: true })));
                window.dispatchEvent(new Event('notifikace-changed'));
            });
    };

    // Obrazovka ukazuje, co čeká — ne archiv. „Čeká" je nepřečtená notifikace
    // a taky přečtená, která pořád chce rozhodnutí (prohoz směny se přečtením
    // nevyřídí). Odbyté se drží stranou a vytáhnou se na požádání.
    const ceka    = notifikace.filter(n => !n.precten || jeAkcniNotifikace(n, user));
    const odbyte  = notifikace.filter(n => n.precten && !jeAkcniNotifikace(n, user));

    // Tlačítko „Nastavit upozornění" — v hlavičce na desktopu, nahoře v seznamu na mobilu.
    const tlacitkoNastaveni = (
        <button
            onClick={() => setNastaveniOtevreno(true)}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg"
            style={{ background: 'var(--surface-elevated)', color: 'var(--text-secondary)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`, cursor: 'pointer' }}
        >
            <Settings2 size={13} />
            Nastavit upozornění
        </button>
    );

    return (
        <div>
            <NastaveniUpozorneni open={nastaveniOtevreno} onClose={() => setNastaveniOtevreno(false)} isDark={isDark} />
            {isMobile && <div className="flex justify-end mb-3">{tlacitkoNastaveni}</div>}
            {/* ── Header — na mobilu se zobrazuje v TopBaru, tady skryjeme ── */}
            {!isMobile && (
                <div className="flex items-center gap-3 mb-6 flex-wrap">
                    <div
                        className="flex items-center justify-center shrink-0"
                        style={{ width: 40, height: 40, borderRadius: 7, background: 'rgba(255,138,0,0.12)' }}
                    >
                        <Bell size={20} style={{ color: '#FF8A00' }} />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Notifikace</h1>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                            {unreadCount > 0 ? `${unreadCount} nepřečtených` : 'Vše přečteno'} &middot; {total} celkem
                        </p>
                    </div>
                    <div className="ml-auto">{tlacitkoNastaveni}</div>
                </div>
            )}

            {/* ── Filter pills + označit přečtené ── */}
            <div
                className="flex flex-wrap gap-2 mb-6 items-center"
                /* Filtry podle modulů jsou nástroj na velký seznam u stolu.
                   Na mobilu řadí seznam očekávání (k vyřízení × pro informaci)
                   a filtry by jen zabraly první obrazovku pod výdechem. */
                hidden={isMobile}
            >
                {unreadCount > 0 && (
                    <button
                        onClick={markAllRead}
                        className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg order-last ml-auto"
                        style={{
                            background: 'rgba(255,138,0,0.12)',
                            color: '#FF8A00',
                            border: isDark ? '1px solid rgba(255,138,0,0.25)' : 'none',
                            cursor: 'pointer', transition: 'all 0.15s ease',
                            ...(isDark ? { backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' } : {}),
                        }}
                    >
                        <CheckCheck size={13} />
                        Označit vše přečtené
                    </button>
                )}
                <FilterPill
                    active={activeModul === 'vse'}
                    onClick={() => handleFilterChange('vse')}
                    label="Vše"
                    color="var(--text-primary)"
                    bg="var(--surface-elevated)"
                    isDark={isDark}
                />
                {availableModuly.map(modul => {
                    const cfg = vzhledModulu(modul);
                    return (
                        <FilterPill
                            key={modul}
                            active={activeModul === modul}
                            onClick={() => handleFilterChange(modul)}
                            label={cfg.label}
                            color={cfg.color}
                            bg={cfg.bg}
                            Icon={cfg.Icon}
                            isDark={isDark}
                        />
                    );
                })}
            </div>

            {/* ── Loading ── */}
            {loading ? (
                <div className="text-center py-16">
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Načítám…</p>
                </div>
            ) : (notifikace.length === 0 && ukoly.length === 0) ? (
                <div className="text-center py-16">
                    <Bell size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 12px', opacity: 0.4 }} />
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Žádné notifikace</p>
                </div>
            ) : (
                <>
                    {/* Co čeká. Bez nadpisu — je to celá náplň obrazovky,
                        cedule „Nepřečtené" nad jediným seznamem nic nepřidá. */}
                    {(ceka.length + ukoly.length) > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {/* Úkoly první — mají termín a nevyřídí se přečtením. */}
                            {ukoly.map(u => (
                                <UkolCard key={u.id} ukol={u} isDark={isDark} />
                            ))}
                            {ceka.map(n => (
                                <AktivitaCard
                                    key={n.id}
                                    notif={n}
                                    user={user}
                                    onNavigate={onNavigate}
                                    onMarkRead={markRead}
                                    resolved={false}
                                    isDark={isDark}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-14">
                            <CheckCheck size={30} style={{ color: '#22C55E', margin: '0 auto 10px', opacity: 0.8 }} />
                            <p className="text-sm font-medium" style={{ color: 'var(--text-primary)', margin: 0 }}>
                                Nic nečeká
                            </p>
                            <p className="text-xs" style={{ color: 'var(--text-muted)', margin: '4px 0 0' }}>
                                Všechno máte přečtené i vyřízené.
                            </p>
                        </div>
                    )}

                    {/* Odbyté na požádání — archiv nemá zabírat obrazovku,
                        která má odpovídat na „co mám udělat". */}
                    {odbyte.length > 0 && (
                        <div className={ceka.length > 0 ? 'mt-8' : 'mt-2'}>
                            <button
                                type="button"
                                onClick={() => setZobrazOdbyte(o => !o)}
                                className="mx-auto flex items-center gap-2 text-sm font-medium px-4 py-2.5 rounded-xl"
                                style={{
                                    background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-elevated)',
                                    color: 'var(--text-secondary)',
                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
                                    cursor: 'pointer',
                                }}
                            >
                                <CheckCheck size={14} />
                                {zobrazOdbyte ? 'Skrýt vyřízené' : `Zobrazit i vyřízené (${odbyte.length})`}
                            </button>

                            {zobrazOdbyte && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                                    {odbyte.map(n => (
                                        <AktivitaCard
                                            key={n.id}
                                            notif={n}
                                            user={user}
                                            onNavigate={onNavigate}
                                            resolved={true}
                                            isDark={isDark}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Donačítání dojezdem, ne tlačítkem: na konci seznamu už
                        člověk čte dál, ne že hledá, čím pokračovat. Hlídka je
                        prázdný prvek pod posledním řádkem — jakmile se objeví
                        ve výřezu, přiletí další dávka. */}
                    {hasMore && (zobrazOdbyte || ceka.length > 0) && (
                        <div ref={hlidka} className="flex justify-center py-8" style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                            {loadingMore ? 'Načítám…' : ''}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// ── Filter pill ────────────────────────────────────────────────
function FilterPill({ active, onClick, label, color, bg, Icon, isDark }) {
    return (
        <button
            onClick={onClick}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg"
            style={{
                background: active ? (bg || 'var(--surface-elevated)') : (isDark ? 'rgba(255,255,255,0.04)' : 'transparent'),
                color: active ? color : 'var(--text-secondary)',
                border: active
                    ? `1px solid ${typeof color === 'string' && color.startsWith('#') ? color + '40' : 'var(--border-default)'}`
                    : (isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid var(--border-default)'),
                cursor: 'pointer', transition: 'all 0.15s ease',
                fontWeight: active ? 600 : 400,
                ...(isDark && active ? { backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' } : {}),
            }}
        >
            {Icon && <Icon size={12} />}
            {label}
        </button>
    );
}

// ── Karta notifikace ───────────────────────────────────────────
function AktivitaCard({ notif, user, onNavigate, onMarkRead, resolved, isDark }) {
    const [hovered, setHovered] = useState(false);
    const [upravuje, setUpravuje] = useState(false);
    const modulCfg = vzhledModulu(notif.modul);
    const ModulIcon = modulCfg.Icon;
    const action = cilNotifikace(notif, user);
    const jeAkcni = !resolved && jeAkcniNotifikace(notif, user);

    const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

    const handleAction = () => {
        if (!notif.precten && onMarkRead) onMarkRead(notif.id);
        if (action) onNavigate(action.page, action.params);
    };

    const handleRozhodnuti = async (souhlas) => {
        const path = cestaRozhodnuti(notif, souhlas);
        if (!path) return;
        await fetch(path, { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf() } });
        if (!notif.precten && onMarkRead) onMarkRead(notif.id);
        window.dispatchEvent(new Event('notifikace-changed'));
    };

    // Vzhled kreslí sdílená karta — stejná v seznamu, ve zvonečku i v toastu.
    const akce = [];
    if (jeAkcni) {
        const popisky = popiskyRozhodnuti(notif);
        akce.push({ id: 'ano', label: popisky.ano, Icon: Check, ton: 'ok', onClick: () => handleRozhodnuti(true) });
        akce.push({ id: 'ne', label: popisky.ne, Icon: X, ton: 'ne', onClick: () => handleRozhodnuti(false) });
        if (upravitelnaNepritomnost(notif)) {
            akce.push({ id: 'upravit', label: 'Upravit', Icon: Pencil, onClick: () => setUpravuje(true) });
        }
    } else if (action && !resolved) {
        akce.push({ id: 'cil', label: action.label, Icon: ArrowRight, onClick: handleAction });
    }

    return (<>
        <NotifikaceKarta
            isDark={isDark}
            Ikona={ModulIcon}
            barva={modulCfg.color}
            stitek={modulCfg.label}
            cas={relativeTime(notif.created_at)}
            typBarva={barvaTypu(notif.typ)}
            titulek={notif.titulek}
            zprava={notif.zprava}
            neprectena={!resolved && !notif.precten}
            ztlumena={!!resolved}
            akce={akce}
            naPrecteno={!resolved && !notif.precten && onMarkRead ? () => onMarkRead(notif.id) : null}
        />
        {upravuje && (
            <UpravitNepritomnost
                nedostupnostId={upravitelnaNepritomnost(notif)}
                isDark={isDark}
                onClose={() => setUpravuje(false)}
                onHotovo={() => {
                    setUpravuje(false);
                    if (!notif.precten && onMarkRead) onMarkRead(notif.id);
                    window.dispatchEvent(new Event('notifikace-changed'));
                }}
            />
        )}
    </>);
}

/**
 * Karta úkolu ze sekce „co mě čeká" v Přehledu — směrnice k podpisu,
 * dokument, nedokončené školení. Kreslí ji tatáž karta jako notifikace, aby
 * seznam působil jako jeden, ne jako dva slepené.
 */
function UkolCard({ ukol, isDark }) {
    const klikatelny = typeof ukol.onClick === 'function';
    const termin = ukol.deadline
        ? new Date(ukol.deadline).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' })
        : null;
    const poTerminu = ukol.deadline && new Date(ukol.deadline) < new Date();
    const barva = ukol.iconColor || '#FF8A00';

    return (
        <NotifikaceKarta
            isDark={isDark}
            Ikona={ukol.icon}
            barva={barva}
            stitek={ukol.subtitle}
            cas={termin ? (poTerminu ? `po termínu (${termin})` : `do ${termin}`) : null}
            titulek={ukol.title}
            naKliknuti={klikatelny ? ukol.onClick : null}
            pod={ukol.progressPct != null && ukol.stav !== 'prirazeno' ? (
                <div style={{ marginTop: 8, height: 4, borderRadius: 2, overflow: 'hidden', background: isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)' }}>
                    <div style={{ height: '100%', borderRadius: 2, width: `${ukol.progressPct}%`, background: barva }} />
                </div>
            ) : null}
        />
    );
}
